/**
 * Contractor profile — Payments & Dues tab.
 *
 * One row per work the contractor holds: contract value, billed, paid, and what is
 * still due either way. Reads GET ds-wms/contractors/:id/payment-summary.
 *   Due to contractor  = bills submitted but not yet paid + retention held
 *   Recoverable        = advance balance not yet recovered + imposed penalties
 *   Net due            = due to contractor - recoverable
 */
import { useCallback, useEffect, useState } from 'react';
import axiosInstance from '@/services/interceptor';
import { TableSkeleton } from '@/components/base/Skeleton';
import { fmtMoney } from '../../lib/money';

interface WorkPayment {
  workId: number;
  workCode: string;
  workName: string;
  workStatus: string;
  udaName: string;
  contractValue: number;
  billsCount: number;
  billed: number;
  paid: number;
  billsDue: number;
  billsDueCount: number;
  lastPaidDate: string | null;
  advancePaid: number;
  advanceRecovered: number;
  advanceBalance: number;
  penaltyDue: number;
  penaltyProposed: number;
  bgActive: number;
  bgExpiryDate: string | null;
  retentionHeld: number;
  balanceToBill: number;
  dueToContractor: number;
  recoverable: number;
}

interface PaymentSummary {
  works: number;
  contractValue: number;
  billed: number;
  paid: number;
  billsDue: number;
  billsDueCount: number;
  advancePaid: number;
  advanceBalance: number;
  penaltyDue: number;
  penaltyProposed: number;
  bgActive: number;
  retentionHeld: number;
  balanceToBill: number;
  dueToContractor: number;
  recoverable: number;
  netDue: number;
}

const fmtDate = (ymd: string | null) => (ymd ? new Date(`${ymd}T00:00:00`).toLocaleDateString('en-IN') : '-');
const money = (v: number) => (v ? fmtMoney(v) : '-');

interface Props {
  contractorId: number;
  onWorkIdClick: (workId: string) => void;
}

export default function ContractorPaymentsTab({ contractorId, onWorkIdClick }: Props) {
  const [rows, setRows] = useState<WorkPayment[]>([]);
  const [summary, setSummary] = useState<PaymentSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!contractorId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(`ds-wms/contractors/${contractorId}/payment-summary`);
      setRows(res.data?.data || []);
      setSummary(res.data?.summary || null);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load payment details');
    } finally {
      setLoading(false);
    }
  }, [contractorId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-4"><TableSkeleton rows={4} columns={8} showToolbar={false} /></div>;

  if (error) {
    return (
      <div className="p-8 text-center text-sm text-red-600">
        <i className="ri-error-warning-line mr-1"></i>{error}
        <button onClick={load} className="ml-3 text-blue-600 hover:underline">Retry</button>
      </div>
    );
  }

  if (rows.length === 0 || !summary) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="text-center">
          <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100">
            <i className="ri-money-rupee-circle-line text-3xl text-gray-400"></i>
          </div>
          <h3 className="text-sm font-semibold text-gray-700">No payments yet</h3>
          <p className="mt-1 text-xs text-gray-500">Contracts, bills, advances and penalties for this contractor will be summarised here.</p>
        </div>
      </div>
    );
  }

  const cards = [
    { label: 'Contract Value', value: fmtMoney(summary.contractValue), sub: `${summary.works} works`, tone: 'text-slate-800' },
    { label: 'Billed', value: fmtMoney(summary.billed), sub: `${fmtMoney(summary.balanceToBill)} left to bill`, tone: 'text-slate-800' },
    { label: 'Paid', value: fmtMoney(summary.paid), sub: 'bills marked Paid', tone: 'text-green-700' },
    { label: 'Bills Due', value: fmtMoney(summary.billsDue), sub: `${summary.billsDueCount} bills awaiting payment`, tone: 'text-amber-700' },
    { label: 'Retention Held', value: fmtMoney(summary.retentionHeld), sub: 'to release to contractor', tone: 'text-amber-700' },
    { label: 'Advance to Recover', value: fmtMoney(summary.advanceBalance), sub: `of ${fmtMoney(summary.advancePaid)} disbursed`, tone: 'text-red-600' },
    { label: 'Penalties Due', value: fmtMoney(summary.penaltyDue),
      sub: summary.penaltyProposed ? `${fmtMoney(summary.penaltyProposed)} proposed` : 'imposed, not recovered', tone: 'text-red-600' },
    { label: 'Active BGs', value: fmtMoney(summary.bgActive), sub: 'bank guarantees held', tone: 'text-slate-800' },
  ];
  const netTone = summary.netDue > 0 ? 'border-amber-200 bg-amber-50 text-amber-800'
    : summary.netDue < 0 ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50 text-green-700';

  return (
    <div className="space-y-3 p-4">
      <div className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border px-4 py-2.5 ${netTone}`}>
        <div className="text-xs">
          <span className="font-semibold">Due to contractor</span> {fmtMoney(summary.dueToContractor)}
          <span className="mx-2 text-gray-400">−</span>
          <span className="font-semibold">Recoverable</span> {fmtMoney(summary.recoverable)}
        </div>
        <div className="text-sm font-bold">
          {summary.netDue >= 0 ? 'Net due to contractor' : 'Net recoverable from contractor'}: {fmtMoney(Math.abs(summary.netDue))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-lg border border-gray-200 bg-white px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{c.label}</div>
            <div className={`text-base font-bold ${c.tone}`}>{c.value}</div>
            <div className="text-[10px] text-gray-400">{c.sub}</div>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Work</th>
              <th className="px-3 py-2 text-left">UDA</th>
              <th className="px-3 py-2 text-right">Contract</th>
              <th className="px-3 py-2 text-right">Billed</th>
              <th className="px-3 py-2 text-right">Paid</th>
              <th className="px-3 py-2 text-right">Bills Due</th>
              <th className="px-3 py-2 text-right">Retention</th>
              <th className="px-3 py-2 text-right">Advance Bal.</th>
              <th className="px-3 py-2 text-right">Penalty</th>
              <th className="px-3 py-2 text-right">Net Due</th>
              <th className="px-3 py-2 text-left">Last Paid</th>
              <th className="px-3 py-2 text-left">BG Expiry</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r) => {
              const net = r.dueToContractor - r.recoverable;
              return (
                <tr key={r.workId} className="hover:bg-gray-50">
                  <td className="max-w-[240px] px-3 py-2">
                    <button onClick={() => onWorkIdClick(String(r.workId))} className="block max-w-full truncate text-left text-blue-600 hover:underline" title={r.workName}>
                      {r.workCode || r.workName}
                    </button>
                    <div className="truncate text-[10px] text-gray-400" title={r.workName}>
                      {r.workName}{r.workStatus ? ` · ${r.workStatus}` : ''}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-gray-600">{r.udaName || '-'}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-gray-700">{money(r.contractValue)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-gray-700">
                    {money(r.billed)}{r.billsCount ? <div className="text-[10px] text-gray-400">{r.billsCount} bills</div> : null}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-green-700">{money(r.paid)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-amber-700">{money(r.billsDue)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-amber-700">{money(r.retentionHeld)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-red-600">{money(r.advanceBalance)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-red-600">{money(r.penaltyDue)}</td>
                  <td className={`whitespace-nowrap px-3 py-2 text-right font-mono font-semibold ${net > 0 ? 'text-amber-800' : net < 0 ? 'text-red-700' : 'text-gray-500'}`}>
                    {net ? `${net < 0 ? '−' : ''}${fmtMoney(Math.abs(net))}` : '-'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-gray-600">{fmtDate(r.lastPaidDate)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-gray-600">{fmtDate(r.bgExpiryDate)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-gray-50 font-semibold text-gray-800">
            <tr>
              <td className="px-3 py-2" colSpan={2}>Total ({summary.works})</td>
              <td className="px-3 py-2 text-right font-mono">{fmtMoney(summary.contractValue)}</td>
              <td className="px-3 py-2 text-right font-mono">{fmtMoney(summary.billed)}</td>
              <td className="px-3 py-2 text-right font-mono text-green-700">{fmtMoney(summary.paid)}</td>
              <td className="px-3 py-2 text-right font-mono text-amber-700">{fmtMoney(summary.billsDue)}</td>
              <td className="px-3 py-2 text-right font-mono text-amber-700">{fmtMoney(summary.retentionHeld)}</td>
              <td className="px-3 py-2 text-right font-mono text-red-600">{fmtMoney(summary.advanceBalance)}</td>
              <td className="px-3 py-2 text-right font-mono text-red-600">{fmtMoney(summary.penaltyDue)}</td>
              <td className="px-3 py-2 text-right font-mono">{`${summary.netDue < 0 ? '−' : ''}${fmtMoney(Math.abs(summary.netDue))}`}</td>
              <td className="px-3 py-2" colSpan={2}></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
